import api from '../../../api/client';
import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Tabs,
  Button,
  Modal,
  Form,
  Input,
  DatePicker,
  Select,
  Table,
  Space,
  Popconfirm,
  message,
  Tag,
  Row,
  Col,
  Tooltip,
  Switch,
  Upload,
  Avatar,
  List,
  Divider,
  Drawer,
  Timeline,
  InputNumber,
  Checkbox,
  Dropdown,
  Menu,
  Typography,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CalendarOutlined,
  PaperClipOutlined,
  CommentOutlined,
  FilterOutlined,
  SendOutlined,
} from "@ant-design/icons";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import interactionPlugin from "@fullcalendar/interaction";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import axios from "axios";
import { useSelector } from "react-redux";
import { CSS } from "@dnd-kit/utilities";
import { DndContext, closestCenter, useSortable } from "@dnd-kit/sortable";
import config from "../../../config";

dayjs.extend(relativeTime);

const { TabPane } = Tabs;
const { TextArea } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { Title } = Typography;

// ==================== Task Details Drawer Component ====================
export const TaskDetailsDrawer = ({
  visible,
  taskId,
  onClose,
  users,
  apiUrl,
  userId,
  onRefresh,
}) => {
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(false);
  const [comments, setComments] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [subtasks, setSubtasks] = useState([]);
  const [sendingComment, setSendingComment] = useState(false);
  const [commentForm] = Form.useForm();
  const [editForm] = Form.useForm();

  useEffect(() => {
    if (visible && taskId) fetchTaskDetails();
  }, [visible, taskId]);

  const fetchTaskDetails = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/api/tasks/${taskId}`);
      setTask(data);
      setComments(data.comments || []);
      setAttachments(data.attachments || []);
      setSubtasks(data.subtasks || []);
      editForm.setFieldsValue({
        title: data.title,
        description: data.description,
        status: data.status,
        priority: data.priority,
        due_date: data.due_date ? dayjs(data.due_date) : null,
        assigned_to: data.assigned_to,
        estimated_hours: data.estimated_hours,
        tags: data.tags ? data.tags.split(",") : [],
        reminder_minutes: data.reminder_minutes,
      });
    } catch (err) {
      message.error("Failed to load task details");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (values) => {
    try {
      const payload = {
        ...values,
        due_date: values.due_date?.format("YYYY-MM-DD HH:mm:ss"),
        tags: values.tags?.join(","),
      };
      await api.put(`/api/tasks/${taskId}`, payload);
      message.success("Task updated");
      fetchTaskDetails();
      onRefresh();
    } catch (err) {
      message.error("Update failed");
    }
  };

  const addComment = async (values) => {
    setSendingComment(true);
    try {
      await api.post(`/api/tasks/${taskId}/comments`, {
        user_id: userId,
        comment: values.comment,
      });
      await fetchTaskDetails();
      commentForm.resetFields();
      message.success("Comment added");
    } catch (err) {
      message.error("Failed to add comment");
    } finally {
      setSendingComment(false);
    }
  };

  const addSubtask = async (values) => {
    try {
      await api.post(`/api/tasks`, {
        ...values,
        parent_task_id: taskId,
        created_by: userId,
      });
      await fetchTaskDetails();
      message.success("Subtask added");
    } catch (err) {
      message.error("Failed to add subtask");
    }
  };

  const updateSubtaskStatus = async (subtaskId, status) => {
    try {
      await api.put(`/api/tasks/${subtaskId}`, { status });
      await fetchTaskDetails();
    } catch (err) {
      message.error("Update failed");
    }
  };

  const uploadAttachment = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("user_id", userId);
    try {
      await api.post(`/api/tasks/${taskId}/attachments`, formData);
      await fetchTaskDetails();
      message.success("Attachment uploaded");
    } catch (err) {
      message.error("Upload failed");
    }
    return false;
  };

  const sendReminder = async () => {
    try {
      await api.post(`/api/tasks/${taskId}/send-reminder`);
      message.success("Reminder sent to assignee");
    } catch (err) {
      message.error("Failed to send reminder");
    }
  };

  if (!task) return null;

  return (
    <Drawer
      title="Task Details"
      width={720}
      open={visible}
      onClose={onClose}
      loading={loading}
    >
      <Form form={editForm} layout="vertical" onFinish={handleUpdate}>
        <Form.Item name="title" label="Title" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name="description" label="Description">
          <TextArea rows={3} />
        </Form.Item>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="status" label="Status">
              <Select>
                <Option value="todo">To Do</Option>
                <Option value="in_progress">In Progress</Option>
                <Option value="review">Review</Option>
                <Option value="done">Done</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="priority" label="Priority">
              <Select>
                <Option value="low">Low</Option>
                <Option value="medium">Medium</Option>
                <Option value="high">High</Option>
                <Option value="urgent">Urgent</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="due_date" label="Due Date">
              <DatePicker
                showTime
                format="YYYY-MM-DD HH:mm:ss"
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="assigned_to" label="Assign To">
              <Select
                options={users.map((u) => ({
                  label: `${u.firstname} ${u.lastname}`,
                  value: u.id,
                }))}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="estimated_hours" label="Est. Hours">
              <InputNumber min={0} step={0.5} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="reminder_minutes"
              label="Reminder (minutes before due)"
            >
              <InputNumber
                min={0}
                style={{ width: "100%" }}
                placeholder="e.g., 60"
              />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="tags" label="Tags">
          <Select mode="tags" placeholder="Add tags" />
        </Form.Item>
        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit">
              Save Changes
            </Button>
            <Button onClick={sendReminder} icon={<SendOutlined />}>
              Send Reminder
            </Button>
          </Space>
        </Form.Item>
      </Form>

      <Divider>Subtasks</Divider>
      <List
        dataSource={subtasks}
        renderItem={(sub) => (
          <List.Item>
            <Checkbox
              checked={sub.status === "done"}
              onChange={(e) =>
                updateSubtaskStatus(sub.id, e.target.checked ? "done" : "todo")
              }
            />
            {sub.title}
            <Tag
              color={sub.priority === "high" ? "red" : "blue"}
              style={{ marginLeft: 8 }}
            >
              {sub.priority}
            </Tag>
          </List.Item>
        )}
      />
      <Form onFinish={addSubtask} layout="inline" style={{ marginTop: 8 }}>
        <Form.Item name="title" rules={[{ required: true }]}>
          <Input placeholder="New subtask" />
        </Form.Item>
        <Form.Item name="priority" initialValue="medium">
          <Select style={{ width: 100 }}>
            <Option value="low">Low</Option>
            <Option value="medium">Medium</Option>
            <Option value="high">High</Option>
          </Select>
        </Form.Item>
        <Form.Item>
          <Button htmlType="submit" type="link">
            + Add
          </Button>
        </Form.Item>
      </Form>

      <Divider>Attachments</Divider>
      <Upload beforeUpload={uploadAttachment} showUploadList={false}>
        <Button icon={<PaperClipOutlined />}>Upload File</Button>
      </Upload>
      <List
        dataSource={attachments}
        renderItem={(att) => (
          <List.Item>
            <a href={`/${att.filepath}`} target="_blank">
              {att.filename}
            </a>
          </List.Item>
        )}
      />

      <Divider>Comments</Divider>
      <Timeline>
        {comments.map((c) => (
          <Timeline.Item
            key={c.id}
            dot={
              <Avatar
                size={24}
                src={`https://ui-avatars.com/api/?name=${c.user_id}`}
              />
            }
          >
            <strong>{users.find((u) => u.id === c.user_id)?.firstname}</strong>
            <div>{c.comment}</div>
            <small>{dayjs(c.created_at).fromNow()}</small>
          </Timeline.Item>
        ))}
      </Timeline>
      <Form form={commentForm} onFinish={addComment}>
        <Form.Item name="comment">
          <TextArea rows={3} placeholder="Add a comment..." />
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit" loading={sendingComment}>
            Post Comment
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
};
